/**
 * Cuántas solicitudes hay sin trabajar, para poder verlo sin entrar.
 *
 * ## Por qué existe
 *
 * Las solicitudes nuevas son una cola de trabajo, y no había forma de saber
 * que había algo esperando sin abrir la pantalla. Quien no entra, no se
 * entera — y una solicitud comercial sin contestar se enfría sola.
 *
 * ## Cada cuánto se refresca, y por qué no es SSE
 *
 * Se pide al abrir, cada minuto, y **al volver a la pestaña**. Ese último es
 * el que de verdad importa: el caso real no es tener el panel delante viendo
 * cambiar un número, es volver de otra cosa y encontrarlo al día.
 *
 * Un canal en vivo para un contador que cambia unas pocas veces al día sería
 * una conexión permanente por cada persona conectada a cambio de nada. Si
 * algún día las solicitudes llegan a un ritmo en que el minuto se note, aquí
 * es donde se cambia.
 *
 * ## Tickets de soporte
 *
 * La entrada «Tickets» cuenta los activos del país elegido, el mismo número
 * que la tarjeta «Activos» de la página: abiertos, en curso, reabiertos y en
 * espera. Al agente, el backend le cuenta sólo los suyos. Sólo se pide para
 * los roles que tienen la entrada en el menú; al resto el endpoint le daría
 * 403. Mientras la página de tickets está abierta, ella misma fija el número
 * con `fijar` cada vez que recarga sus estadísticas (también por SSE), así el
 * menú y las tarjetas no se contradicen y no se pide dos veces lo mismo.
 *
 * ## Un fallo no rompe el menú
 *
 * Si la petición falla, el contador se queda como estaba y no se pinta nada.
 * El menú tiene que funcionar aunque este número no se pueda calcular.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useCountry } from './CountryContext';
import { advisorLeadService } from '@/services/advisor-lead.service';
import { merchantLeadService } from '@/services/merchant-lead.service';
import { merchantRecommendationService } from '@/services/merchant-recommendation.service';
import { supportTicketService } from '@/services/support-ticket.service';
import type { StaffRole } from '@/types/auth.types';

/** Cada cuánto se vuelve a preguntar mientras la pestaña está abierta. */
const CADA_MS = 60_000;

/** Ruta de la entrada del menú de tickets. */
export const TICKETS_HREF = '/support-tickets';

/** Los roles con «Tickets» en el menú, que son los que admite el endpoint. */
const ROLES_TICKETS: ReadonlySet<StaffRole> = new Set<StaffRole>([
  'SUPER_ADMIN',
  'SUPPORT_LEAD',
  'SUPPORT_AGENT',
]);

interface PendingLeadsValue {
  /** Ruta del menú → cuántas esperan. Sin entrada = nada que mostrar. */
  pendientes: Record<string, number>;
  refrescar: () => void;
  /** Fija el número de una entrada con un dato que ya se tiene a mano. */
  fijar: (href: string, cuantos: number) => void;
}

const PendingLeadsContext = createContext<PendingLeadsValue>({
  pendientes: {},
  refrescar: () => {},
  fijar: () => {},
});

export function PendingLeadsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { selectedCountryId } = useCountry();
  const role = user?.profiles?.staff?.role as StaffRole | undefined;
  const veTickets = !!role && ROLES_TICKETS.has(role);
  const [pendientes, setPendientes] = useState<Record<string, number>>({});

  // Evita que dos disparos solapados —el intervalo y el foco— se pisen.
  const enCurso = useRef(false);

  // País de la última consulta pedida. Una respuesta de otro país que llega
  // tarde no debe pintar su número en el menú del país actual.
  const paisActual = useRef(selectedCountryId);
  useEffect(() => {
    paisActual.current = selectedCountryId;
  }, [selectedCountryId]);

  const consultarTickets = useCallback(async () => {
    const pais = selectedCountryId;
    if (!isAuthenticated || !veTickets || !pais) {
      setPendientes((previo) => {
        if (!(TICKETS_HREF in previo)) return previo;
        const siguiente = { ...previo };
        delete siguiente[TICKETS_HREF];
        return siguiente;
      });
      return;
    }
    try {
      const stats = await supportTicketService.getStats(pais);
      if (paisActual.current !== pais) return;
      setPendientes((previo) => ({
        ...previo,
        [TICKETS_HREF]: stats.activeCount,
      }));
    } catch {
      // El contador se queda como estaba.
    }
  }, [isAuthenticated, veTickets, selectedCountryId]);

  const consultar = useCallback(async () => {
    if (!isAuthenticated) return;
    void consultarTickets();
    if (enCurso.current) return;
    enCurso.current = true;
    try {
      // `limit: 1` porque sólo interesa el total del paginado: traerse las
      // filas para contarlas sería pedir datos que nadie va a mirar.
      const [comercios, asesores, recomendaciones] = await Promise.allSettled([
        merchantLeadService.getAll({ status: 'NEW', limit: 1 }),
        advisorLeadService.getAll({ status: 'NEW', limit: 1 }),
        // Sin las duplicadas: no generan bono ni gestión propia, así que
        // contarlas inflaría una cola que nadie tiene que trabajar.
        merchantRecommendationService.getAll({ status: 'NEW', limit: 1 }),
      ]);

      setPendientes((previo) => {
        const siguiente = { ...previo };
        if (comercios.status === 'fulfilled') {
          siguiente['/leads'] = comercios.value.meta?.total ?? 0;
        }
        if (asesores.status === 'fulfilled') {
          siguiente['/advisor-leads'] = asesores.value.meta?.total ?? 0;
        }
        if (recomendaciones.status === 'fulfilled') {
          siguiente['/recommendations'] =
            recomendaciones.value.meta?.total ?? 0;
        }
        return siguiente;
      });
    } finally {
      enCurso.current = false;
    }
  }, [isAuthenticated, consultarTickets]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPendientes({});
      return;
    }

    void consultar();
    const intervalo = setInterval(() => void consultar(), CADA_MS);

    // Volver a la pestaña es el momento en que el número importa.
    const alVolver = () => {
      if (document.visibilityState === 'visible') void consultar();
    };
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [isAuthenticated, consultar]);

  const fijar = useCallback((href: string, cuantos: number) => {
    setPendientes((previo) =>
      previo[href] === cuantos ? previo : { ...previo, [href]: cuantos }
    );
  }, []);

  const value = useMemo<PendingLeadsValue>(
    () => ({ pendientes, refrescar: () => void consultar(), fijar }),
    [pendientes, consultar, fijar]
  );

  return (
    <PendingLeadsContext.Provider value={value}>
      {children}
    </PendingLeadsContext.Provider>
  );
}

export function usePendingLeads(): PendingLeadsValue {
  return useContext(PendingLeadsContext);
}
